import { FC, ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';

import { isImpersonationAllowed, logout } from '../../store/authentication/actions';

import ImpersonationDialog from './ImpersonationDialog';
import { AuthenticationState } from '../../store/authentication/state';
import './MyAccountDropDown.scss';
import { DropDownMenu } from '../common/DropDownMenu';
import { BOLINDA_ICON_BOLINDA_ONLY } from '../common/Button/Icon';
import { log } from '../utils/log';
import ModalDialog from '../components/modal/ModalDialog';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Utils from '../utils';
import { showNotification } from '../notification/toastNotification';

type Props = {
    readonly authentication: AuthenticationState;
    readonly findUsers: () => void;
    readonly impersonate: (user: string) => void;
};

const MyAccountDropDown: FC<Props> = ({ authentication, findUsers, impersonate }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const [showImpersonationDialog, setShowImpersonationDialog] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [reportMessage, setReportMessage] = useState('');
    const reset = () => {
        setShowReportModal(false);
        setReportMessage('');
    };
    const reportBug = () => {
        log.error(`User Report - ${reportMessage}`, {
            message: reportMessage,
            causeBy: 'User Report'
        });
        reset();
        showNotification(t('notifications.reportBug.error'));
    };

    function loadImpersonation() {
        findUsers();
        setShowImpersonationDialog(true);
    }

    function onLogout() {
        logout(authentication.kc);
    }

    const styles = {
        iconFontSize: { fontSize: '18px' }
    };

    const auth = authentication;
    const kc = auth.kc;
    const accountUrl = kc.createAccountUrl();
    const users = auth.impersonation ? auth.impersonation.users || [] : [];
    const usersLoading = auth.impersonation ? auth.impersonation.loading : false;

    let impersonationLink: ReactNode = null;
    if (isImpersonationAllowed(kc)) {
        impersonationLink = (
            <li>
                <a onClick={loadImpersonation}>
                    <i className="bolinda-icons upper fl-left" style={styles.iconFontSize}>
                        {BOLINDA_ICON_BOLINDA_ONLY}
                    </i>
                    Impersonation
                </a>
            </li>
        );
    }

    return (
        <>
            <ImpersonationDialog
                show={showImpersonationDialog}
                users={users}
                loading={usersLoading}
                findUsers={findUsers}
                onClose={() => setShowImpersonationDialog(false)}
                onImpersonate={impersonate}
            />
            <DropDownMenu
                label={t('components.accountDropdown.myAccount')}
                className="my-account just-dd expanded-dd dd-a-r fl-none"
                arrowClassName="md-uppest"
                expanded={expanded}
                onToggleClick={setExpanded}
                onClickOutside={setExpanded}
                align="right"
            >
                <li>
                    <Link to="/orders" className="lk-view-order">
                        <i className="material-icons uppest fl-left">receipt</i>
                        {t('components.accountDropdown.viewOrders')}
                    </Link>
                </li>
                <li>
                    <a href={accountUrl}>
                        <i className="material-icons uppest fl-left">person</i>
                        {t('components.accountDropdown.accountDetails')}
                    </a>
                </li>
                <li>
                    <a onClick={onLogout}>
                        <i className="material-icons upper fl-left">input</i>
                        {t('components.accountDropdown.logout')}
                    </a>
                </li>
                {impersonationLink}
                {Utils.isUserAdmin(authentication) ? (
                    <li>
                        <a
                            onClick={() => {
                                setExpanded(false);
                                setShowReportModal(true);
                            }}
                        >
                            <i className="bolinda-icons upper fl-left" style={styles.iconFontSize}>
                                {BOLINDA_ICON_BOLINDA_ONLY}
                            </i>
                            {t('components.accountDropdown.sendReport')}
                        </a>
                    </li>
                ) : null}
            </DropDownMenu>

            <ModalDialog
                show={showReportModal}
                title={t('components.accountDropdown.sendReport')}
                onCloseButtonClick={reset}
            >
                <StBody>
                    <p> {t('components.accountDropdown.reportDesc')}</p>
                    <textarea
                        value={reportMessage}
                        onChange={event => setReportMessage(event.target.value)}
                        placeholder={t('components.accountDropdown.issueDesc')}
                        rows={5}
                        autoFocus={true}
                    />
                    <br />
                    <StFooter>
                        <a className="lk" onClick={reset}>
                            {t('cancel')}
                        </a>
                        <a className="lk lk-primary" onClick={reportBug}>
                            {t('send')}
                        </a>
                    </StFooter>
                </StBody>
            </ModalDialog>
        </>
    );
};

export default MyAccountDropDown;

const StBody = styled.div`
    textarea {
        width: 100%;
        height: auto;
        border: 1px solid #979797;
        border-radius: 3px;
        padding: 2px;
    }
`;

const StFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 20px;
    width: 100%;
    gap: 15px;
`;
